import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError, type DisputeCase, type DisputeProposal, type Dispute, type DisputeProposalInput } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { errorText, ACTIVE_CASE_STATUSES } from "./model";
import { AgreementContext } from "./SettlementTerms";
import { ContinuationConfirmation } from "./CaseDialogs";

export function useCaseConversation({ id, onBack, desktopBack }: { id: string; onBack: () => void; desktopBack: boolean }) {
  const { user } = useAuth();
  const [caseData, setCaseData] = useState<DisputeCase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftImage, setDraftImage] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [accepting, setAccepting] = useState<DisputeProposal | null>(null);
  const [acceptedProposalTerms, setAcceptedProposalTerms] = useState(false);
  const [markingUnresolved, setMarkingUnresolved] = useState(false);
  const [unresolvedReason, setUnresolvedReason] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const continuationRequestId = useRef(crypto.randomUUID());
  const [continuationReview, setContinuationReview] = useState<string | null>(null);
  const [showingDetails, setShowingDetails] = useState(false);
  const actions = useRef<HTMLDetailsElement>(null);
  function closeActions() {
    if (actions.current) {
      actions.current.open = false;
      actions.current.querySelector("summary")?.focus();
    }
  }
  useEffect(() => {
    const dismiss = (event: PointerEvent) => {
      if (event.target instanceof Node && actions.current && !actions.current.contains(event.target)) actions.current.open = false;
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && actions.current?.open) closeActions();
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, []);
  const alive = useRef(true), pending = useRef(false), request = useRef(0), scroll = useRef<HTMLDivElement>(null);
  const seen = useRef(0), atBottom = useRef(true);
  const accessLost = useRef(false);
  function rejectLostAccess(failure: unknown, reading = false): boolean {
    const lost = failure instanceof ApiError && (failure.status === 401 || failure.code === "profile_unavailable" ||
      failure.code === "dispute_access_unavailable" || (reading && [403, 404].includes(failure.status)));
    if (!lost) return false;
    // [disputes.inbox-and-history](../../../../docs/product/disputes.md#disputes-inbox-and-history): definitive access loss clears protected content and every pending
    // action. Late refreshes cannot restore it; transient failures keep the draft.
    accessLost.current = true; ++request.current;
    setUnavailable(true); setCaseData(null); setError(null);
    setShowingDetails(false);
    setProposing(false); setAccepting(null); setMarkingUnresolved(false); setDraft(""); setDraftImage(null);
    setWithdrawing(false); setContinuationReview(null); setUnresolvedReason("");
    return true;
  }
  async function refresh() {
    if (accessLost.current) return;
    const generation = ++request.current;
    try {
      const next = await api.disputeCase(id);
      if (!alive.current || accessLost.current || generation !== request.current) return;
      setCaseData(previous => {
        const messages = new Map([...(previous?.messages || []), ...next.messages].map(message => [message.id, message]));
        return { ...next, hasMore: previous && previous.messages[0]?.sequence < next.messages[0]?.sequence ? previous.hasMore : next.hasMore,
          messages: [...messages.values()].sort((a, b) => a.sequence - b.sequence) };
      });
      if (!ACTIVE_CASE_STATUSES.includes(next.dispute.status)) {
        setAccepting(null); setProposing(false); setMarkingUnresolved(false); setDraft(""); setDraftImage(null);
        setWithdrawing(false); setContinuationReview(null); setUnresolvedReason("");
      } else if (next.dispute.status === "unresolved") {
        setMarkingUnresolved(false); setUnresolvedReason("");
      }
      if (document.visibilityState === "visible" && (next.dispute.viewerRole !== "admin" || next.dispute.assignedAdminUserId === user?.id)) {
        const sequence = next.messages[next.messages.length - 1]?.sequence || 0;
        if (sequence > seen.current) {
          await api.disputeAction(id, "read", { sequence });
          if (alive.current) { seen.current = sequence; window.dispatchEvent(new Event("dispute-read")); }
        }
      }
    } catch (failure: unknown) {
      if (alive.current && generation === request.current && !rejectLostAccess(failure, true)) setError(errorText(failure));
    }
  }
  useEffect(() => {
    alive.current = true;
    void refresh();
    const tick = () => { if (document.visibilityState === "visible" && !pending.current) void refresh(); };
    const timer = setInterval(tick, 10000);
    window.addEventListener("focus", tick); document.addEventListener("visibilitychange", tick);
    return () => { alive.current = false; ++request.current; clearInterval(timer); window.removeEventListener("focus", tick); document.removeEventListener("visibilitychange", tick); };
  }, [id]);
  useEffect(() => { if (atBottom.current && scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight; }, [caseData?.messages.length]);
  async function run(action: () => Promise<unknown>, success?: () => void) {
    if (pending.current || accessLost.current) return;
    pending.current = true; setBusy(true); setError(null);
    try {
      await action();
      if (!alive.current || accessLost.current) return;
      success?.(); await refresh();
    } catch (failure: unknown) {
      if (alive.current && !accessLost.current && !rejectLostAccess(failure)) { setError(errorText(failure)); void refresh(); }
    } finally { pending.current = false; if (alive.current) setBusy(false); }
  }
  async function earlier() {
    if (!caseData || pending.current) return;
    await run(async () => {
      const previous = await api.disputeCase(id, caseData.messages[0]?.sequence);
      if (!alive.current || accessLost.current) return;
      atBottom.current = false;
      setCaseData(current => current ? { ...current, hasMore: previous.hasMore, messages: [...previous.messages, ...current.messages] } : current);
    });
  }
  async function reviewNewProposal(input: DisputeProposalInput) {
    setError(null);
    try { return (await api.previewDisputeProposal(id, input)).review; }
    catch (failure) { rejectLostAccess(failure); throw failure; }
  }
  async function continueAgreement(action: () => Promise<{ dispute: Dispute }>) {
    const result = await action();
    if (!alive.current || accessLost.current) return;
    // [disputes.continuation-outcome](../../../../docs/product/disputes.md#disputes-continuation-outcome): the committed closure immediately disables drafts and financial
    // actions, even when the following history refresh temporarily fails.
    ++request.current;
    setCaseData(currentCase => currentCase ? { ...currentCase, dispute: result.dispute, paymentExecution: "unchanged" } : currentCase);
    setAccepting(null); setProposing(false); setMarkingUnresolved(false); setDraft(""); setDraftImage(null);
    setWithdrawing(false); setContinuationReview(null); setUnresolvedReason("");
  }
  async function markUnresolved() {
    const { dispute } = await api.markDisputeUnresolved(id, unresolvedReason.trim());
    if (!alive.current || accessLost.current) return;
    // [disputes.unresolved](../../../../docs/product/disputes.md#disputes-unresolved): reflect the committed status even if history refresh fails;
    // discussion drafts and financial proposals stay available for later consent.
    ++request.current;
    setCaseData(currentCase => currentCase ? { ...currentCase, dispute } : currentCase);
    setMarkingUnresolved(false); setUnresolvedReason("");
  }
  if (unavailable) return { state: "unavailable" as const };
  if (!caseData) return { state: "loading" as const, error, refresh };
  const d = caseData.dispute, admin = d.viewerRole === "admin", active = ACTIVE_CASE_STATUSES.includes(d.status);
  const joined = admin && d.assignedAdminUserId === user?.id && !!d.adminJoinedAt;
  const canWithdraw = active && !admin && d.canWithdraw === true;
  const continuation = caseData.continuations?.find(item => item.status === "pending");
  const canMarkUnresolved = active && joined && !d.adminUnavailable && d.status !== "unresolved";
  const withdrawalDialog = withdrawing && <ContinuationConfirmation title="Withdraw and continue?" busy={busy} disabled={!canWithdraw || !!continuation} error={error}
    description="Ask the other party to close this dispute and resume the agreement at its prior stage. No payment moves. Discussion and payment proposals remain available while you wait."
    label="Send request" onClose={() => setWithdrawing(false)}
    onConfirm={() => void run(() => api.requestDisputeContinuation(id, continuationRequestId.current), () => { setWithdrawing(false); continuationRequestId.current = crypto.randomUUID(); })} />;
  const current = caseData.proposals.find(proposal => proposal.version === d.proposalVersion);
  const reviewed = accepting && current?.id === accepting.id ? current : accepting;
  const changedReview = !!reviewed && (current?.id !== reviewed.id || current?.status !== "pending");
  const agreementContext = <AgreementContext dispute={d} agreement={caseData.agreement} busy={busy} onDownload={() => void run(() => api.downloadDisputeDocument(id, "agreement"))} />;
  function submitMessage(event: FormEvent) {
    event.preventDefault();
    if (!draft.trim() && !draftImage) return;
    const submittedDraft = draft, submittedImage = draftImage;
    void run(() => api.sendDisputeMessage(id, submittedDraft.trim(), submittedImage), () => {
      setDraft(currentDraft => currentDraft === submittedDraft ? "" : currentDraft);
      setDraftImage(currentImage => currentImage === submittedImage ? null : currentImage);
      atBottom.current = true;
    });
  }
  return {
    state: "ready" as const, id, onBack, desktopBack, user, caseData, error, setError, draft,
    setDraft, draftImage, setDraftImage, busy, proposing, setProposing, setAccepting,
    acceptedProposalTerms, setAcceptedProposalTerms, markingUnresolved, setMarkingUnresolved,
    unresolvedReason, setUnresolvedReason, setWithdrawing, continuationReview,
    setContinuationReview, showingDetails, setShowingDetails, actions, closeActions, pending,
    scroll, atBottom, rejectLostAccess, refresh, run, earlier, reviewNewProposal,
    continueAgreement, markUnresolved, d, admin, active, joined, canWithdraw, continuation,
    canMarkUnresolved, withdrawalDialog, current, reviewed, changedReview, agreementContext,
    submitMessage
  };
}
export type CaseConversationModel = Extract<ReturnType<typeof useCaseConversation>, { state: "ready" }>;
